import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  useCreateTodoMutation,
  useListTodoCategoriesQuery,
} from '../../redux/api/todoApi';
import { useApiErrorToast } from '../../hooks/useApiError';

const CreateTodo = () => {
  const navigate = useNavigate();
  const currentEvent = useSelector((state) => state.auth.currentEvent);
  const apiErrorToast = useApiErrorToast();

  const { data: categories } = useListTodoCategoriesQuery();
  const [createTodo, { isLoading }] = useCreateTodoMutation();
  const [fieldErrors, setFieldErrors] = useState({});

  const [formValues, setFormValues] = useState({
    title: '',
    description: '',
    category: '',
    priority: 'NORMAL',
    due_at: '',
  });

  const update = (k, v) => setFormValues((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldErrors({});
    if (!currentEvent?.id) {
      toast.error('Bitte zuerst ein Event auswählen.');
      return;
    }
    if (!formValues.title || !formValues.category) {
      toast.error('Titel und Kategorie sind Pflichtfelder.');
      return;
    }
    const payload = {
      event: currentEvent.id,
      title: formValues.title,
      description: formValues.description,
      category: formValues.category,
      priority: formValues.priority,
      due_at: formValues.due_at ? new Date(formValues.due_at).toISOString() : null,
    };
    try {
      const created = await createTodo(payload).unwrap();
      toast.success('Aufgabe angelegt');
      navigate(`/todo/${created.id}`);
    } catch (err) {
      const e = err?.data || err;
      if (e?.code === 'validation_error' && e.details) {
        setFieldErrors(e.details);
      }
      apiErrorToast(err, 'Aufgabe konnte nicht angelegt werden');
    }
  };

  const fieldFeedback = (name) =>
    fieldErrors[name] && (
      <div className='invalid-feedback d-block'>
        {Array.isArray(fieldErrors[name])
          ? fieldErrors[name].join(' ')
          : String(fieldErrors[name])}
      </div>
    );

  return (
    <div className='col-md-8 mx-auto'>
      <h4>Neue Aufgabe</h4>
      <form onSubmit={handleSubmit}>
        <div className='mb-3'>
          <label className='form-label'>Titel</label>
          <input
            type='text'
            className={`form-control ${fieldErrors.title ? 'is-invalid' : ''}`}
            value={formValues.title}
            onChange={(e) => update('title', e.target.value)}
            required
          />
          {fieldFeedback('title')}
        </div>

        <div className='mb-3'>
          <label className='form-label'>Beschreibung</label>
          <textarea
            className={`form-control ${fieldErrors.description ? 'is-invalid' : ''}`}
            rows={3}
            value={formValues.description}
            onChange={(e) => update('description', e.target.value)}
          />
          {fieldFeedback('description')}
        </div>

        <div className='row'>
          <div className='col-md-6 mb-3'>
            <label className='form-label'>Kategorie</label>
            <select
              className={`form-select ${fieldErrors.category ? 'is-invalid' : ''}`}
              value={formValues.category}
              onChange={(e) => update('category', e.target.value)}
              required
            >
              <option value=''>— wählen —</option>
              {categories?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            {fieldFeedback('category')}
          </div>

          <div className='col-md-3 mb-3'>
            <label className='form-label'>Priorität</label>
            <select
              className='form-select'
              value={formValues.priority}
              onChange={(e) => update('priority', e.target.value)}
            >
              <option value='LOW'>niedrig</option>
              <option value='NORMAL'>normal</option>
              <option value='HIGH'>hoch</option>
            </select>
          </div>

          <div className='col-md-3 mb-3'>
            <label className='form-label'>Fällig (optional)</label>
            <input
              type='datetime-local'
              className='form-control'
              value={formValues.due_at}
              onChange={(e) => update('due_at', e.target.value)}
            />
          </div>
        </div>

        <button type='submit' className='btn btn-primary' disabled={isLoading}>
          Aufgabe anlegen
        </button>
        <button
          type='button'
          className='btn btn-link ms-2'
          onClick={() => navigate('/todo')}
        >
          Abbrechen
        </button>
      </form>
    </div>
  );
};

export default CreateTodo;
